/**
 * `gltfImageDedup.ts` — collapse identical embedded glTF images onto one
 * decode, at parse time, before `GLTFLoader` mints a blob URL per `images[]`
 * row.
 *
 * A second exporter shape (2026-09-11, live "Noble scene" iPhone crash)
 * stores only four image rows but writes hundreds of identical sampler rows.
 * Three's texture cache key is `imageSource:samplerIndex`, so those duplicate
 * sampler indexes still force hundreds of 2048² GPU texture uploads. This
 * plugin canonicalizes identical sampler rows before the image-source remap,
 * collapsing both duplicate-image and duplicate-sampler assets through the
 * same loader cache.
 *
 * ## Why this exists (2026-09-10, follow-up to the drone load)
 *
 * `textureBudget.ts` caps how BIG a texture may be. It does not collapse
 * copies. The original 2048 mobile ceiling made the drone GLB's 2048² maps a
 * no-op for size; the later 1024 ceiling helps, but duplicate rows would still
 * multiply decode/upload cost.
 *
 * AI-1339 shares S3 *customer* textures in `CampfireMaterialBuilder`. It never
 * sees embedded GLB `images[]`. Unity's `ImportTextures = false` skips those
 * rows entirely; the web still loads every one, because a web import made
 * before AI-1494 has empty `MaterialIDs` and the embedded images are its only
 * pixels (and a new import reads them to build its texture objects).
 *
 * Measured on the reported Exalted / Monumental / Dron volando_ GLB (one S3
 * object, 26.8 MB): 80 `images[]` entries, 80 distinct bufferViews (full
 * copies, not shared views), **4 unique images by content**. One 228 KB JPEG
 * is stored 77 times (~17.8 MB of wasted BIN). Decode-all is 1.28 GB CPU RGBA
 * / ~1.71 GB GPU with mips; unique is 64 MB / 85 MB. iPhone Safari tabs
 * typically jetsam around 1.2–1.8 GB — the same crash class as the Bike-test
 * 8K maps, a different hole.
 *
 * ## The seam
 *
 * A `GLTFLoader.register` plugin whose `beforeRoot` runs *before*
 * `getDependencies('scene')` (three's parse, ~line 2653). It hashes each
 * referenced `images[]` row (bufferView bytes + mime, or the uri string) and
 * rewrites `textures[i].source` — plus any `texture.extensions[ext].source`
 * number, covering `KHR_texture_basisu` / `EXT_texture_webp` — onto the first
 * index with that content. `loadImageSource` keys `sourceCache` by image
 * index, so 77 textures pointing at image 0 decode once and then clone.
 *
 * Do **not** rewrite `images[]` or the BIN. Unused rows are fine if nothing
 * `getDependency('image', i)`s them. Do **not** implement plugin
 * `loadBufferView` — `_invokeOne` would intercept geometry views too. Do
 * **not** use `crypto.subtle`: it is secure-context-only, and a LAN-IP /
 * Vision Pro http session would silently skip the whole pass.
 *
 * Fail-soft: a hash that throws leaves that image unique. Idempotent: a
 * second pass finds textures already on the canonical index and remaps 0.
 */

const K_FNV32_OFFSET = 0x811c9dc5;
const K_FNV32_PRIME = 0x01000193;

export type GltfImageDef = {
  uri?: string;
  bufferView?: number;
  mimeType?: string;
};

export type GltfTextureDef = {
  source?: number;
  sampler?: number;
  extensions?: Record<string, unknown>;
};

export type GltfSamplerDef = Record<string, unknown>;

export type GltfJsonLike = {
  images?: GltfImageDef[];
  samplers?: GltfSamplerDef[];
  textures?: GltfTextureDef[];
};

export type GltfImageDedupParser = {
  json: GltfJsonLike;
  getDependency: (type: string, index: number) => Promise<unknown>;
};

export type GltfImageDedupResult = {
  totalImageCount: number;
  uniqueImageCount: number;
  remappedTextureCount: number;
  totalSamplerCount: number;
  uniqueSamplerCount: number;
  remappedSamplerCount: number;
};

export type ImageContentInput =
  | { kind: "bytes"; mimeType: string; bytes: Uint8Array }
  | { kind: "uri"; uri: string }
  | { kind: "unique" };

/**
 * FNV-1a 32-bit. Fast, sync, works on an insecure origin. Collision of two
 * different payloads is then rejected by a byte compare in
 * {@link assignCanonicalImageIndexes}.
 */
export function fnv1a32(bytes: Uint8Array): number {
  let hash = K_FNV32_OFFSET;
  for (let i = 0; i < bytes.length; i++) {
    hash ^= bytes[i];
    hash = Math.imul(hash, K_FNV32_PRIME);
  }
  return hash >>> 0;
}

export function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function contentHashKey(mimeType: string, bytes: Uint8Array): string {
  return `${mimeType}|${bytes.byteLength}|${fnv1a32(bytes).toString(16)}`;
}

/**
 * First-wins canonical index per content. URI rows match on the string
 * (do not fetch). Byte rows match on hash then exact bytes, so a 32-bit
 * collision of different payloads stays unique.
 */
export function assignCanonicalImageIndexes(
  inputs: ReadonlyArray<ImageContentInput>,
): number[] {
  const canonical = inputs.map((_, i) => i);
  const firstByUri = new Map<string, number>();
  const firstByHash = new Map<string, Array<{ index: number; bytes: Uint8Array }>>();

  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i];
    if (input.kind === "unique") continue;
    if (input.kind === "uri") {
      const existing = firstByUri.get(input.uri);
      if (existing === undefined) firstByUri.set(input.uri, i);
      else canonical[i] = existing;
      continue;
    }
    const hashKey = contentHashKey(input.mimeType, input.bytes);
    const bucket = firstByHash.get(hashKey);
    if (!bucket) {
      firstByHash.set(hashKey, [{ index: i, bytes: input.bytes }]);
      continue;
    }
    const match = bucket.find((entry) => bytesEqual(entry.bytes, input.bytes));
    if (match) canonical[i] = match.index;
    else bucket.push({ index: i, bytes: input.bytes });
  }
  return canonical;
}

function addReferencedSource(
  set: Set<number>,
  value: unknown,
  imageCount: number,
): void {
  if (typeof value !== "number" || value < 0 || value >= imageCount) return;
  set.add(value);
}

/**
 * Image indexes a texture (or a texture extension such as
 * `KHR_texture_basisu`) actually names. Missing `source` is glTF's default 0.
 */
export function collectReferencedImageIndexes(
  textures: GltfTextureDef[] | undefined,
  imageCount: number,
): Set<number> {
  const set = new Set<number>();
  if (!textures || imageCount <= 0) return set;
  for (const tex of textures) {
    addReferencedSource(set, tex.source ?? 0, imageCount);
    const exts = tex.extensions;
    if (!exts) continue;
    for (const value of Object.values(exts)) {
      if (value && typeof value === "object") {
        addReferencedSource(set, (value as { source?: unknown }).source, imageCount);
      }
    }
  }
  return set;
}

function remapSourceField(
  holder: { source?: number },
  canonical: ReadonlyArray<number>,
): boolean {
  if (typeof holder.source !== "number") return false;
  if (holder.source < 0 || holder.source >= canonical.length) return false;
  const next = canonical[holder.source];
  if (next === holder.source) return false;
  holder.source = next;
  return true;
}

/**
 * Rewrite `textures[].source` (and extension `source` numbers) onto
 * `canonical[old]`. Does not touch `images[]`.
 */
export function remapDuplicateGltfImageSources(
  json: GltfJsonLike,
  canonical: ReadonlyArray<number>,
): number {
  const textures = json.textures;
  if (!textures) return 0;
  let remapped = 0;
  for (const tex of textures) {
    if (remapSourceField(tex, canonical)) remapped++;
    const exts = tex.extensions;
    if (!exts) continue;
    for (const value of Object.values(exts)) {
      if (value && typeof value === "object" && remapSourceField(value as { source?: number }, canonical)) {
        remapped++;
      }
    }
  }
  return remapped;
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson((value as Record<string, unknown>)[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function assignCanonicalSamplerIndexes(
  samplers: ReadonlyArray<GltfSamplerDef> | undefined,
): number[] {
  if (!samplers) return [];
  const canonical = samplers.map((_, i) => i);
  const firstByValue = new Map<string, number>();
  for (let i = 0; i < samplers.length; i++) {
    const key = stableJson(samplers[i] ?? {});
    const existing = firstByValue.get(key);
    if (existing === undefined) firstByValue.set(key, i);
    else canonical[i] = existing;
  }
  return canonical;
}

export function remapDuplicateGltfSamplers(
  json: GltfJsonLike,
  canonical: ReadonlyArray<number>,
): number {
  const textures = json.textures;
  if (!textures || canonical.length === 0) return 0;
  let remapped = 0;
  for (const tex of textures) {
    if (typeof tex.sampler !== "number") continue;
    if (tex.sampler < 0 || tex.sampler >= canonical.length) continue;
    const next = canonical[tex.sampler];
    if (next === tex.sampler) continue;
    tex.sampler = next;
    remapped++;
  }
  return remapped;
}

function asUint8Array(dep: unknown): Uint8Array | null {
  if (dep instanceof ArrayBuffer) return new Uint8Array(dep);
  if (ArrayBuffer.isView(dep)) {
    return new Uint8Array(dep.buffer, dep.byteOffset, dep.byteLength);
  }
  return null;
}

async function readImageInput(
  parser: GltfImageDedupParser,
  image: GltfImageDef,
): Promise<ImageContentInput> {
  if (typeof image.bufferView === "number") {
    const dep = await parser.getDependency("bufferView", image.bufferView);
    const bytes = asUint8Array(dep);
    if (!bytes) return { kind: "unique" };
    return { kind: "bytes", mimeType: image.mimeType ?? "", bytes };
  }
  if (typeof image.uri === "string" && image.uri.length > 0) {
    return { kind: "uri", uri: image.uri };
  }
  return { kind: "unique" };
}

function uniqueReferencedCount(
  canonical: ReadonlyArray<number>,
  referenced: ReadonlySet<number>,
): number {
  const seen = new Set<number>();
  for (const index of referenced) seen.add(canonical[index] ?? index);
  return seen.size;
}

/**
 * Hash referenced images and remap texture sources. Returns counts even when
 * nothing moved, so a harness can assert the no-op path.
 */
export async function dedupeGltfImagesOnParser(
  parser: GltfImageDedupParser,
): Promise<GltfImageDedupResult> {
  const json = parser.json;
  const images = json.images;
  const empty: GltfImageDedupResult = {
    totalImageCount: images?.length ?? 0,
    uniqueImageCount: images?.length ?? 0,
    remappedTextureCount: 0,
    totalSamplerCount: json.samplers?.length ?? 0,
    uniqueSamplerCount: json.samplers?.length ?? 0,
    remappedSamplerCount: 0,
  };
  const samplerCanonical = assignCanonicalSamplerIndexes(json.samplers);
  const uniqueSamplerCount =
    samplerCanonical.length > 0 ? new Set(samplerCanonical).size : empty.uniqueSamplerCount;
  const remappedSamplerCount = remapDuplicateGltfSamplers(json, samplerCanonical);

  if (!images || images.length <= 1) {
    return {
      ...empty,
      uniqueSamplerCount,
      remappedSamplerCount,
    };
  }

  const referenced = collectReferencedImageIndexes(json.textures, images.length);
  if (referenced.size <= 1) {
    return {
      ...empty,
      uniqueSamplerCount,
      remappedSamplerCount,
    };
  }

  const inputs: ImageContentInput[] = images.map(() => ({ kind: "unique" }));
  const jobs: Promise<void>[] = [];
  for (const index of referenced) {
    const image = images[index];
    if (!image) continue;
    jobs.push(
      (async () => {
        try {
          inputs[index] = await readImageInput(parser, image);
        } catch (err) {
          console.warn("[gltfImageDedup] failed to hash image", index, err);
          inputs[index] = { kind: "unique" };
        }
      })(),
    );
  }
  await Promise.all(jobs);

  const canonical = assignCanonicalImageIndexes(inputs);
  const remappedTextureCount = remapDuplicateGltfImageSources(json, canonical);
  return {
    totalImageCount: images.length,
    uniqueImageCount: uniqueReferencedCount(canonical, referenced),
    remappedTextureCount,
    totalSamplerCount: json.samplers?.length ?? 0,
    uniqueSamplerCount,
    remappedSamplerCount,
  };
}

export function installGltfImageDedupPlugin(parser: GltfImageDedupParser): {
  name: string;
  beforeRoot: () => Promise<void>;
} {
  return {
    name: "CAMPFIRE_gltf_image_dedup",
    beforeRoot: async () => {
      const result = await dedupeGltfImagesOnParser(parser);
      if (result.remappedTextureCount > 0 || result.remappedSamplerCount > 0) {
        console.info(
          `[gltfImageDedup] collapsed ${result.remappedTextureCount} duplicate texture sources ` +
            `(${result.totalImageCount} images → ${result.uniqueImageCount} unique decodes); ` +
            `${result.remappedSamplerCount} duplicate samplers ` +
            `(${result.totalSamplerCount} samplers → ${result.uniqueSamplerCount} unique)`,
        );
      }
    },
  };
}
